import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next';
import moment from 'moment'
import { useRecoilState } from 'recoil';
import { globalParameterColorState } from '../../../plugins/recoil';

const GraphComponent = ({ chartWidth, chartHeight, numericData, parameter, selectBoxTimeInfo, timeRanges, UnitType }) => {
    const { t } = useTranslation()
    const [parameterColors, ] = useRecoilState(globalParameterColorState);
    const [parameterData, setParameterData] = useState([]);
    const [otherParameterDatas, setOtherParameterDatas] = useState([]); //ibp 시리즈,nipb일 경우    

    // ✅ useEffect로 데이터 처리
    useEffect(() => {
        //console.log('numericData : ',numericData)
        if (parameter === 'ibp1' || parameter === 'ibp2' || parameter === 'ibp3' || parameter === 'ibp4' || parameter === 'nibp') {
            const resultData = numericData
                .map(item => {
                    const sysKey = `${parameter}sys`;
                    const diaKey = `${parameter}dia`;
                    const mapKey = `${parameter}map`;

                    // sys, dia, map 데이터 추출
                    const sysData = item[sysKey];
                    const diaData = item[diaKey];
                    const mapData = item[mapKey];

                    // 최소한 하나의 값이라도 유효한 경우에만 처리
                    if ((sysData?.value && sysData.value !== '-') ||
                        (diaData?.value && diaData.value !== '-') ||
                        (mapData?.value && mapData.value !== '-')) {

                        return {
                            timestamp: item.timestamp,
                            sys: {
                                value: sysData?.value && sysData.value !== '-' 
                                    ? parseFloat(sysData.value) 
                                    : null,
                                alarm: sysData?.alarm,
                                priority: sysData?.priority
                            },
                            dia: {
                                value: diaData?.value && diaData.value !== '-' 
                                    ? parseFloat(diaData.value) 
                                    : null,
                                alarm: diaData?.alarm,
                                priority: diaData?.priority
                            },
                            map: {
                                value: mapData?.value && mapData.value !== '-' 
                                    ? parseFloat(mapData.value) 
                                    : null,
                                alarm: mapData?.alarm,
                                priority: mapData?.priority
                            }
                        };
                    }
                    return null;
                })
                .filter(item => item !== null)
                .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
            
                
            setOtherParameterDatas(resultData);  
            //console.log('Other Parameter Data:', resultData);          
        }else{
            // 현재 파라미터에 해당하는 데이터만 추출
            const resultData = numericData
                .map(item => {
                    if (item[parameter] && 
                        item[parameter].value && 
                        item[parameter].value !== '-' && 
                        item[parameter].value !== '') {
                        
                        const numericValue = parseFloat(item[parameter].value);
                        //const numericValue = Math.floor(Math.random() * 251) - 50; //테스트 용
                        
                        if (!isNaN(numericValue)) {
                            return {
                                timestamp: item.timestamp,
                                value: numericValue,
                                alarm: item[parameter].alarm,
                                priority: item[parameter].priority
                            };
                        }
                    }
                    return null;
                })
                .filter(item => item !== null)
                .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

            setParameterData(resultData);
        }
    }, [numericData, parameter]);

    // ✅ 계산값들을 useMemo로 메모이제이션
    const { minY, maxY, range, values } = useMemo(() => {
        if (parameterData.length === 0) {
            return { minY: 0, maxY: 100, range: 100, values: [] };
        }
        
        const values = parameterData.map(d => d.value);
        const combinedWave = parameterData.flatMap(d => d.value);
        //console.log('combinedWave', combinedWave);
        
        const dataMin = Math.min(...combinedWave);
        const dataMax = Math.max(...combinedWave);
        const dataRange = dataMax - dataMin;
        
        let minY, maxY;
        
        // ✅ 모든 값이 같을 때 (range가 0일 때) 처리
        if (dataRange === 0) {
            // 고정된 범위 설정 (현재 값 ±50)
            minY = dataMin - 50;
            maxY = dataMax + 50;
        } else {
            // 기존 로직 (범위의 20%, 30% 여백)
            minY = dataMin - (dataRange * 0.2);
            maxY = dataMax + (dataRange * 0.3);
        }

        const range = maxY - minY || 100; // 최소 범위 100 보장
        
        return { minY, maxY, range, values };
    }, [parameterData]);

    // ✅ 함수들을 컴포넌트 내부에 정의
    const getX = (timestamp) => {
        const timeIndex = timeRanges.findIndex(timeRange => 
            moment(timeRange).isSame(moment(timestamp), 'second')
        );
        
        if (timeIndex === -1) return 0;
        return (timeIndex * 170);
    };

    const getY = (value) => {
        const ratio = (value - minY) / range;
        return chartHeight - (ratio * chartHeight);
    };

    // ✅ polylinePoints와 selectedPoint를 useMemo로 계산
    const polylinePoints = useMemo(() => {
        return parameterData
            .map(d => `${getX(d.timestamp)},${getY(d.value)}`)
            .join(' ');
    }, [parameterData, minY, range, chartHeight, timeRanges]);

    const selectedPoint = useMemo(() => {
        return parameterData.find(d => 
            moment(d.timestamp).isSame(moment(selectBoxTimeInfo), 'second')
        );
    }, [parameterData, selectBoxTimeInfo]);    

    const { otherMinY, otherMaxY, otherRange } = useMemo(() => {
        if (otherParameterDatas.length === 0) {
            return { otherMinY: 0, otherMaxY: 200, otherRange: 200 };
        }
        
        // 모든 sys, dia, map 값들을 수집
        const allValues = [];
        otherParameterDatas.forEach(d => {
            if (d.sys?.value !== null) allValues.push(d.sys.value);
            if (d.dia?.value !== null) allValues.push(d.dia.value);
            if (d.map?.value !== null) allValues.push(d.map.value);
        });
        
        if (allValues.length === 0) {
            return { otherMinY: 0, otherMaxY: 200, otherRange: 200 };
        }
        
        const dataMin = Math.min(...allValues);
        const dataMax = Math.max(...allValues);
        const dataRange = dataMax - dataMin;
        
        let otherMinY, otherMaxY;
        
        // ✅ 범위에 따른 처리
        if (dataRange === 0) {
            // 모든 값이 같을 때
            otherMinY = dataMin - 50;
            otherMaxY = dataMax + 50;
        } else if (dataRange < 25) {
            // 데이터 범위가 25 미만일 때 비례 여백 + 최소 여백 보장
            const padding = Math.max(3, dataRange * 0.5);
            otherMinY = dataMin - padding;
            otherMaxY = dataMax + padding;
        } else if (dataRange < 100) {
            // 데이터 범위가 25~100일 때
            const expandedRange = Math.max(100, dataRange * 1.4);
            otherMinY = dataMin - (expandedRange - dataRange) * 0.5;
            otherMaxY = dataMax + (expandedRange - dataRange) * 0.5;
        } else {
            // 데이터 범위가 100 이상일 때
            otherMinY = dataMin - (dataRange * 0.4);
            otherMaxY = dataMax + (dataRange * 0.2);
        }
        
        const otherRange = otherMaxY - otherMinY || 100;
        
        return { otherMinY, otherMaxY, otherRange };
    }, [otherParameterDatas]);

    // ✅ IBP/NIBP용 Y좌표 계산 함수
    const getOtherY = (value) => {
        const ratio = (value - otherMinY) / otherRange;
        return chartHeight - (ratio * chartHeight);
    };

    // ✅ 선택된 IBP/NIBP 포인트 찾기
    const selectedOtherPoint = useMemo(() => {
        return otherParameterDatas.find(d => 
            moment(d.timestamp).isSame(moment(selectBoxTimeInfo), 'second')
        );
    }, [otherParameterDatas, selectBoxTimeInfo]);

    //데이터가 없으면 No Data Available 세팅하고 리턴
    if (parameter === 'ibp1' || parameter === 'ibp2' || parameter === 'ibp3' || parameter === 'ibp4' || parameter === 'nibp') {
        if (otherParameterDatas.length === 0) {
        return (
            <svg className="absolute inset-0 w-full h-full overflow-visible" preserveAspectRatio="none">
                <text
                    x="925"
                    y="50%"
                    textAnchor="middle"
                    fill="#666"
                    fontSize="14"
                    fontFamily="Pretendard"
                >
                    {t('error.noDataAvailable')}
                </text>
            </svg>
        );
    }
    }else {
        if (parameterData.length === 0) {
            return (
                <svg className="absolute inset-0 w-full h-full overflow-visible" preserveAspectRatio="none">
                    <text
                        x="925"
                        y="50%"
                        textAnchor="middle"
                        fill="#666"
                        fontSize="14"
                        fontFamily="Pretendard"
                    >
                        {t('error.noDataAvailable')}
                    </text>
                </svg>
            );
        }
    }
    
    // ✅ 정상적인 JSX 반환
    return (
        <>
            {                
                (parameter === 'ibp1' || parameter === 'ibp2' || parameter === 'ibp3' || parameter === 'ibp4' || parameter === 'nibp') ? (
                    <svg className="absolute inset-0 w-full h-full overflow-visible" preserveAspectRatio="none">
                        {/* 배경 그리드 라인 */}
                        {[1, 2, 3, 4].map((i) => (
                            <line
                                key={`grid-${i}`}
                                x1="0"
                                y1={chartHeight * i / 4}
                                x2={chartWidth}
                                y2={chartHeight * i / 4}
                                stroke="#4E4E4E"
                                strokeWidth="1"
                            />
                        ))}
                        
                        {otherParameterDatas.map((d, i) => {
                            const x = getX(d.timestamp);
                            const sysY = d.sys?.value != null ? getOtherY(d.sys.value) : null;
                            const mapY = d.map?.value != null ? getOtherY(d.map.value) : null;
                            const diaY = d.dia?.value != null ? getOtherY(d.dia.value) : null;

                            return (
                                <g key={`bp-${i}`}>
                                    {/* 수직선 (sys에서 dia까지) */}
                                    {sysY != null && diaY != null && (
                                        <line
                                            x1={x}
                                            y1={sysY}
                                            x2={x}
                                            y2={diaY}
                                            stroke={parameterColors?.['numericParameterColors']?.[UnitType?.[parameter]] || "#FF6B6B"}
                                            strokeWidth="2"
                                        />
                                    )}
                                    
                                    {/* MAP 수평선 */}
                                    {mapY != null && (
                                        <line
                                            x1={x - 8}
                                            y1={mapY}
                                            x2={x + 8}
                                            y2={mapY}
                                            stroke={parameterColors?.['numericParameterColors']?.[UnitType?.[parameter]] || "#FF6B6B"}
                                            strokeWidth="2"
                                        />
                                    )}
                                    
                                    {/* SYS 상단 수평선 */}
                                    {sysY != null && (
                                        <line
                                            x1={x - 8}
                                            y1={sysY}
                                            x2={x + 8}
                                            y2={sysY}
                                            stroke={parameterColors?.['numericParameterColors']?.[UnitType?.[parameter]] || "#FF6B6B"}
                                            strokeWidth="2"
                                        />
                                    )}
                                    
                                    {/* DIA 하단 수평선 */}
                                    {diaY != null && (
                                        <line
                                            x1={x - 8}
                                            y1={diaY}
                                            x2={x + 8}
                                            y2={diaY}
                                            stroke={parameterColors?.['numericParameterColors']?.[UnitType?.[parameter]] || "#FF6B6B"}
                                            strokeWidth="2"
                                        />
                                    )}
                                </g>
                            );
                        })}

                        {/* 선택된 포인트 강조 */}
                        {selectedOtherPoint && (
                            <g>
                                {(() => {
                                    const x = getX(selectedOtherPoint.timestamp);
                                    const sysY = selectedOtherPoint.sys?.value != null ? getOtherY(selectedOtherPoint.sys.value) : null;
                                    const mapY = selectedOtherPoint.map?.value != null ? getOtherY(selectedOtherPoint.map.value) : null;
                                    const diaY = selectedOtherPoint.dia?.value != null ? getOtherY(selectedOtherPoint.dia.value) : null;
                                    
                                    const minGap = 10; // SYS, MAP, DIA 텍스트가 겹치지 않도록 최소 간격(10px) 보장
                                    let adjustedSysY = sysY != null ? sysY + 5 : null;
                                    let adjustedMapY = mapY != null ? mapY + 5 : null;
                                    let adjustedDiaY = diaY != null ? diaY + 5 : null;

                                    if (adjustedSysY != null && adjustedMapY != null && adjustedMapY - adjustedSysY < minGap) {
                                        adjustedMapY = adjustedSysY + minGap;
                                    }
                                    if (adjustedMapY != null && adjustedDiaY != null && adjustedDiaY - adjustedMapY < minGap) {
                                        adjustedDiaY = adjustedMapY + minGap;
                                    }
                                    if (adjustedSysY != null && adjustedMapY == null && adjustedDiaY != null && adjustedDiaY - adjustedSysY < minGap) {
                                        adjustedDiaY = adjustedSysY + minGap;
                                    }

                                    return (
                                        <>
                                            {adjustedSysY != null && selectedOtherPoint.sys?.value != null && (
                                                <text
                                                    x={x + 12}
                                                    y={adjustedSysY}
                                                    textAnchor="start"
                                                    fill={parameterColors?.['numericParameterColors']?.[UnitType?.[parameter]] || '#FFF'}
                                                    fontSize="12"
                                                    fontFamily="Pretendard"
                                                >
                                                    {selectedOtherPoint.sys.value}
                                                </text>
                                            )}
                                            
                                            {adjustedMapY != null && selectedOtherPoint.map?.value != null && (
                                                <text
                                                    x={x + 12}
                                                    y={adjustedMapY}
                                                    textAnchor="start"
                                                    fill={parameterColors?.['numericParameterColors']?.[UnitType?.[parameter]] || '#FFF'}
                                                    fontSize="12"
                                                    fontFamily="Pretendard"
                                                >
                                                    {selectedOtherPoint.map.value}
                                                </text>
                                            )}
                                            
                                            {adjustedDiaY != null && selectedOtherPoint.dia?.value != null && (
                                                <text
                                                    x={x + 12}
                                                    y={adjustedDiaY}
                                                    textAnchor="start"
                                                    fill={parameterColors?.['numericParameterColors']?.[UnitType?.[parameter]] || '#FFF'}
                                                    fontSize="12"
                                                    fontFamily="Pretendard"
                                                >
                                                    {selectedOtherPoint.dia.value}
                                                </text>
                                            )}
                                        </>
                                    );
                                })()}
                            </g>
                        )}
                    </svg>
                ) : (
                    <svg className="absolute inset-0 w-full h-full overflow-visible" preserveAspectRatio="none">                        
                        {[1, 2, 3, 4].map((i) => (
                            <line
                                key={`grid-${i}`}
                                x1="0"
                                y1={chartHeight * i / 4}
                                x2={chartWidth}
                                y2={chartHeight * i / 4}
                                stroke="#4E4E4E"
                                strokeWidth="1"
                            />
                        ))}

                        {/* Polyline 그래프 */}
                        {parameterData.length > 1 && (
                            <polyline
                                fill="none"
                                stroke={parameterColors?.['numericParameterColors']?.[UnitType?.[parameter]] || "#00FF88"}
                                strokeWidth="2"
                                points={polylinePoints}
                            />
                        )}

                        {/* 모든 데이터 포인트 */}
                        {parameterData.map((d, i) => (
                            <circle
                                key={`point-${i}`}
                                cx={getX(d.timestamp)}
                                cy={getY(d.value)}
                                r="3"
                                fill={parameterColors?.['numericParameterColors']?.[UnitType?.[parameter]] || "#00FF88"}
                            />
                        ))}

                        {/* 선택된 포인트 강조 */}
                        {selectedPoint && (
                            <g>
                                <circle
                                    cx={getX(selectedPoint.timestamp)}
                                    cy={getY(selectedPoint.value)}
                                    r="5"
                                    fill={parameterColors?.['numericParameterColors']?.[UnitType?.[parameter]] || "#00FF88"}
                                    stroke="#FFF"
                                    strokeWidth="2"
                                />
                                
                                <text
                                    x={getX(selectedPoint.timestamp)}
                                    y={getY(selectedPoint.value) - 10}
                                    textAnchor="middle"
                                    fill={parameterColors?.['numericParameterColors']?.[UnitType?.[parameter]] || "#FFF"}
                                    fontSize="12"
                                    fontFamily="Pretendard"
                                >
                                    {selectedPoint.value}
                                </text>
                            </g>
                        )}
                    </svg>
                )
            }
        </>
        
    );
};

export default GraphComponent;